import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Monthly_navbar from './Monthlytasks_navbar';
import moment from 'moment';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { scrollToTop } from '../Shared/ScrollOnTop';
import config from '../../Config';
import FeePostingBusyOverlay from '../Shared/FeePostingBusyOverlay';

const POST_TIMEOUT_MS = 120000;
/** Same endpoint & payload shape as NewStudentList “Generate admission challan (selected)”. */
const ADMISSION_POST_TIMEOUT_MS = 20 * 60 * 1000;

const MONTH_OPTIONS = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

function defaultMonthString() {
    return String(new Date().getMonth() + 1);
}

function defaultYearString() {
    return moment().format('YYYY');
}

function endOfMonthForPosting(monthStr, yearStr) {
    const m = parseInt(monthStr, 10);
    const y = parseInt(yearStr, 10);
    if (!Number.isFinite(m) || !Number.isFinite(y) || m < 1 || m > 12) {
        return moment().endOf('month').format('YYYY-MM-DD');
    }
    return moment({ year: y, month: m - 1, date: 1 }).endOf('month').format('YYYY-MM-DD');
}

const SingleStudentMonthlyChallan = () => {
    document.title = 'Single student — challan (monthly / admission / advance)';
    const [challanKind, setChallanKind] = useState('monthly'); // 'monthly' | 'admission' | 'advance'
    const [searchMode, setSearchMode] = useState('reg');
    const [searchInput, setSearchInput] = useState('');
    const [student, setStudent] = useState(null);
    const [searchBusy, setSearchBusy] = useState(false);

    const [Month, setMonth] = useState(() => defaultMonthString());
    const [Year, setYear] = useState(() => defaultYearString());
    const [dueDate, setDueDate] = useState(() => endOfMonthForPosting(defaultMonthString(), defaultYearString()));
    const [admissionDueDate, setAdmissionDueDate] = useState(() =>
        moment().endOf('month').format('YYYY-MM-DD')
    );
    const [advanceAmount, setAdvanceAmount] = useState('');
    const [advanceDescription, setAdvanceDescription] = useState('Advance Fee');
    const [hideAdvanceInfo, setHideAdvanceInfo] = useState(false);
    const [hideAdmissionInfo, setHideAdmissionInfo] = useState(false);
    const [hideLastPostedInfo, setHideLastPostedInfo] = useState(false);
    const [postTransportFee, setPostTransportFee] = useState(true);
    const [fYearList, setFYearList] = useState([]);
    const [fYearsLoaded, setFYearsLoaded] = useState(false);

    const [lastChalanHeadId, setLastChalanHeadId] = useState(null);
    const [lastChallanNo, setLastChallanNo] = useState('');

    const [AccessKey] = useState(localStorage.getItem('AccessKey'));
    const [UserID] = useState(localStorage.getItem('ID'));
    const [MemberType] = useState(localStorage.getItem('MemberType'));
    const [SessionID] = useState(localStorage.getItem('DefaultSession'));

    const [busyMode, setBusyMode] = useState(null);
    const [busyTitle, setBusyTitle] = useState('');
    const [busyDetail, setBusyDetail] = useState('');
    const [elapsedSec, setElapsedSec] = useState(0);
    const busyTimerRef = useRef(null);

    const isBusy = busyMode !== null;

    const clearBusyTimer = () => {
        if (busyTimerRef.current) {
            clearInterval(busyTimerRef.current);
            busyTimerRef.current = null;
        }
    };

    const startBusyOverlay = (title, detail, mode) => {
        setBusyTitle(title);
        setBusyDetail(detail);
        setBusyMode(mode);
        setElapsedSec(0);
        clearBusyTimer();
        busyTimerRef.current = setInterval(() => setElapsedSec((n) => n + 1), 1000);
    };

    const stopBusyOverlay = () => {
        clearBusyTimer();
        setBusyMode(null);
        setElapsedSec(0);
    };

    useEffect(() => () => clearBusyTimer(), []);

    useEffect(() => {
        const apiData = {
            AccessKey,
            UserID,
            MemberType,
            PageNo: 1,
            PageSize: 100,
        };
        axios({
            method: 'post',
            url: config.base_url + 'Accounts/GetFYears',
            headers: { 'Content-Type': 'application/json' },
            data: apiData,
        })
            .then((response) => {
                const list = response.data?.ListofRecords || [];
                setFYearList(Array.isArray(list) ? list : []);
            })
            .catch(() => setFYearList([]))
            .finally(() => setFYearsLoaded(true));
    }, [AccessKey, UserID, MemberType]);

    useEffect(() => {
        if (!fYearList.length) return;
        const resolveYear = (prev) => {
            const p = String(prev || '');
            if (fYearList.some((item) => String(item.FYear) === p)) return p;
            const cur = defaultYearString();
            if (fYearList.some((item) => String(item.FYear) === cur)) return cur;
            return String(fYearList[0].FYear);
        };
        setYear((prev) => resolveYear(prev));
    }, [fYearList]);

    useEffect(() => {
        if (!Month || !Year) return;
        setDueDate(endOfMonthForPosting(Month, Year));
    }, [Month, Year]);

    const handleSearch = () => {
        const raw = (searchInput || '').trim();
        if (!raw) {
            toast.error('Enter registration number or student id.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        setSearchBusy(true);
        setStudent(null);
        setLastChalanHeadId(null);
        setLastChallanNo('');
        setHideAdvanceInfo(false);
        setHideAdmissionInfo(false);

        const base = {
            AccessKey,
            UserID: UserID,
            MemberType: MemberType || 'Admin',
        };

        const req =
            searchMode === 'id'
                ? axios.post(config.base_url + 'Student/GetStudentByIDWithoutHistory', {
                      ...base,
                      ID: parseInt(raw, 10),
                  })
                : axios.post(config.base_url + 'Student/GetStudentByRegistrationNo', {
                      ...base,
                      Registration_No: raw,
                  });

        req
            .then((res) => {
                if (res.data?.status_code === 1 && res.data.Student) {
                    setStudent(res.data.Student);
                    toast.success('Student loaded.', { position: toast.POSITION.TOP_RIGHT });
                } else {
                    toast.error(res.data?.status_message || 'Student not found.', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            })
            .catch(() => {
                toast.error('Could not load student.', { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(() => setSearchBusy(false));
    };

    const handlePostChallan = () => {
        if (!student || !student.Student_ID) {
            toast.error('Search and select a student first.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!fYearsLoaded || !fYearList.length) {
            toast.error('Financial years are still loading.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!Month || !Year) {
            toast.error('Select month and year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const sm = parseInt(Month, 10);
        const fy = parseInt(Year, 10);
        if (!Number.isFinite(sm) || !Number.isFinite(fy) || sm < 1 || sm > 12) {
            toast.error('Invalid month or year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!dueDate || !moment(dueDate, 'YYYY-MM-DD', true).isValid()) {
            toast.error('Select a valid challan due date.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const data = {
            AccessKey,
            UserID: UserID,
            MemberType: MemberType || 'Admin',
            SchoolID: 1,
            SelectedMonth: sm,
            FYear: fy,
            SessionID,
            AddTransportFee: postTransportFee,
            ChallanDueDate: `${dueDate}T12:00:00`,
            Student_ID: student.Student_ID,
        };

        startBusyOverlay(
            'Posting monthly challan (one student)',
            'Writing tblChalanHead and tblADLedger using the same rules as Start New Month (Challan).',
            'post'
        );

        axios({
            method: 'post',
            url: config.base_url + 'ChallanFeeSystem/PostSingleStudentMonthlyChallan',
            timeout: POST_TIMEOUT_MS,
            headers: { 'Content-Type': 'application/json' },
            data,
        })
            .then((response) => {
                if (response.data.status_code === 1) {
                    setLastChalanHeadId(response.data.chalan_head_id);
                    setLastChallanNo(response.data.challan_no || '');
                    setHideLastPostedInfo(false);
                    toast.success(response.data.status_message || 'Posted.', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                } else {
                    toast.error(response.data.status_message || response.data.status_Message || 'Request failed', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                }
            })
            .catch((error) => {
                const msg =
                    error.code === 'ECONNABORTED'
                        ? 'Request timed out.'
                        : error.response?.data?.status_message || error.message || 'Network error.';
                toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(stopBusyOverlay);
    };

    const handlePostAdmissionChallan = () => {
        if (!student || !student.Student_ID) {
            toast.error('Search and select a student first.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (
            !admissionDueDate ||
            !moment(admissionDueDate, 'YYYY-MM-DD', true).isValid()
        ) {
            toast.error('Select a valid admission challan due date.', {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        const data = {
            AccessKey,
            UserID: parseInt(UserID, 10) || 0,
            MemberType: MemberType || 'Admin',
            SelectedStudents: [student.Student_ID],
            ChallanDueDate: `${admissionDueDate}T12:00:00`,
        };

        startBusyOverlay(
            'Posting admission challan (one student)',
            'Same rules as New Student List: class Admission (A) fees, optional transport (005), tblChalanHead + tblADLedger.',
            'postAdmission'
        );

        axios({
            method: 'post',
            url: config.base_url + 'ChallanFeeSystem/PostAdmissionChallansForNewStudents',
            timeout: ADMISSION_POST_TIMEOUT_MS,
            headers: { 'Content-Type': 'application/json' },
            data,
        })
            .then((response) => {
                if (response.data.status_code === 1) {
                    const ids = response.data.chalan_head_ids;
                    const first =
                        Array.isArray(ids) && ids.length > 0 ? Number(ids[0]) : null;
                    setLastChalanHeadId(first != null && first > 0 ? first : null);
                    setLastChallanNo('');
                    setHideLastPostedInfo(false);
                    if (Array.isArray(ids) && ids.length > 0) {
                        sessionStorage.setItem(
                            'lastAdmissionChallanHeadIds',
                            JSON.stringify(ids)
                        );
                    }
                    toast.success(
                        response.data.status_message ||
                            'Admission challan request completed.',
                        { position: toast.POSITION.TOP_RIGHT }
                    );
                } else {
                    toast.error(
                        response.data.status_message ||
                            response.data.status_Message ||
                            'Request failed',
                        { position: toast.POSITION.TOP_RIGHT }
                    );
                }
            })
            .catch((error) => {
                const msg =
                    error.code === 'ECONNABORTED'
                        ? 'Request timed out.'
                        : error.response?.data?.status_message ||
                          error.message ||
                          'Network error.';
                toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(stopBusyOverlay);
    };

    const handlePostAdvanceChallan = () => {
        if (!student || !student.Student_ID) {
            toast.error('Search and select a student first.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!String(student.Registration_No || '').trim()) {
            toast.error('Advance challan requires a registered student (registration number).', {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        if (!fYearsLoaded || !fYearList.length) {
            toast.error('Financial years are still loading.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        if (!Month || !Year) {
            toast.error('Select month and year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const sm = parseInt(Month, 10);
        const fy = parseInt(Year, 10);
        if (!Number.isFinite(sm) || !Number.isFinite(fy) || sm < 1 || sm > 12) {
            toast.error('Invalid month or year.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        const amt = parseFloat(String(advanceAmount).replace(/,/g, '').trim());
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error('Enter a valid advance amount greater than zero.', {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }
        if (!dueDate || !moment(dueDate, 'YYYY-MM-DD', true).isValid()) {
            toast.error('Select a valid challan due date.', { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const data = {
            AccessKey,
            UserID: UserID,
            MemberType: MemberType || 'Admin',
            SelectedMonth: sm,
            FYear: fy,
            ChallanDueDate: `${dueDate}T12:00:00`,
            Student_ID: student.Student_ID,
            Fee_Amount: amt,
            Description: (advanceDescription || 'Advance Fee').trim(),
        };

        startBusyOverlay(
            'Posting advance challan (one student)',
            'Creating tblChalanHead (ChallanType Advance) and tblADLedger line fee 020 — Advance Fee.',
            'postAdvance'
        );

        axios({
            method: 'post',
            url: config.base_url + 'ChallanFeeSystem/PostSingleStudentAdvanceChallan',
            timeout: POST_TIMEOUT_MS,
            headers: { 'Content-Type': 'application/json' },
            data,
        })
            .then((response) => {
                if (response.data.status_code === 1) {
                    setLastChalanHeadId(response.data.chalan_head_id);
                    setLastChallanNo(response.data.challan_no || '');
                    setHideLastPostedInfo(false);
                    toast.success(response.data.status_message || 'Advance challan posted.', {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                } else {
                    toast.error(
                        response.data.status_message || response.data.status_Message || 'Request failed',
                        { position: toast.POSITION.TOP_RIGHT }
                    );
                }
            })
            .catch((error) => {
                const msg =
                    error.code === 'ECONNABORTED'
                        ? 'Request timed out.'
                        : error.response?.data?.status_message || error.message || 'Network error.';
                toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
            })
            .finally(stopBusyOverlay);
    };

    const wingName = student?.SelectedClass?.SelectedWing?.Wing_Name || '';
    const className = student?.SelectedClass?.Class_Name || '';
    const sectionName = student?.SelectedSection?.Section_Name || '';
    const routeName = student?.SelectedRoot?.Root_Name || '';

    return (
        <>
            <FeePostingBusyOverlay
                show={isBusy}
                title={busyTitle}
                message={busyDetail}
                elapsedSeconds={elapsedSec}
            />
            <Monthly_navbar />
            <ToastContainer />
            <div className="page-head">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/MonthlyTasks/MonthlyTasksDashboard">Monthly Task</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Single student challan
                                    </li>
                                </ol>
                            </nav>
                            <div className="page-title">Single student — monthly, admission, or advance challan</div>
                        </div>
                        <div className="col-md-4 text-md-end">
                            <Link to="/MonthlyTasks/StartNewMonthChallan" className="button button-white button-icon">
                                Full month posting
                                <i className="ri-arrow-right-line"></i>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <div className="container">
                    <div className="card mb-3">
                        <div className="card-body p-md-4">
                            <p className="mb-2">
                                <strong>Monthly:</strong> same algorithm as <strong>Start New Month (Challan)</strong> for
                                one student (M fees, optional transport, policies; concession on tuition code 013 only; prior-month unpaid lines carried per fee type on <strong>tblADLedger</strong>).{' '}
                                <strong>Admission:</strong> same API as{' '}
                                <Link to="/students/NewStudentList">New Student List</Link> — class{' '}
                                <strong>Admission (A)</strong> fee structure, optional <strong>005</strong> when not
                                already in A lines.{' '}
                                <strong>Advance:</strong> registered students only — one challan (ChallanType{' '}
                                <strong>Advance</strong>) with fee line <strong>020 Advance Fee</strong>; amount you
                                enter is posted to <strong>tblADLedger</strong> (payment receipt steps follow later).
                            </p>
                            <p className="mb-0 text-muted small">
                                <strong>Monthly</strong> requires that period in <strong>StartedMonth</strong> and refuses
                                if a monthly challan with lines already exists for that month.{' '}
                                <strong>Admission</strong> only creates challans for students <strong>without</strong> a
                                registration number.{' '}
                                <strong>Advance</strong> requires a <strong>registration number</strong>; each post creates
                                a new Advance challan using the standard YYMM+sequence+StudentId challan number.
                            </p>
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body p-md-4">
                            <h5 className="mb-3">Find student</h5>
                            <div className="row g-2 align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label">Search by</label>
                                    <select
                                        className="form-select"
                                        value={searchMode}
                                        onChange={(e) => setSearchMode(e.target.value)}
                                        disabled={searchBusy || isBusy}
                                    >
                                        <option value="reg">Registration no.</option>
                                        <option value="id">Student ID</option>
                                    </select>
                                </div>
                                <div className="col-md-5">
                                    <label className="form-label">
                                        {searchMode === 'id' ? 'Student ID' : 'Registration number'}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        disabled={searchBusy || isBusy}
                                        placeholder={searchMode === 'id' ? 'e.g. 12345' : 'e.g. DPSK0925'}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <button
                                        type="button"
                                        className="button button-primary"
                                        onClick={() => {
                                            scrollToTop();
                                            handleSearch();
                                        }}
                                        disabled={searchBusy || isBusy}
                                    >
                                        {searchBusy ? 'Searching…' : 'Load student'}
                                    </button>
                                </div>
                            </div>

                            {student && (
                                <div className="table-responsive mt-4">
                                    <table className="table table-bordered table-sm mb-0">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: '180px' }}>Student ID</th>
                                                <td>{student.Student_ID}</td>
                                            </tr>
                                            <tr>
                                                <th>Registration</th>
                                                <td>{student.Registration_No || '—'}</td>
                                            </tr>
                                            <tr>
                                                <th>Name</th>
                                                <td>{student.Student_Name}</td>
                                            </tr>
                                            <tr>
                                                <th>Father</th>
                                                <td>{student.Father_Name}</td>
                                            </tr>
                                            <tr>
                                                <th>Wing / Class / Section</th>
                                                <td>
                                                    {[wingName, className, sectionName].filter(Boolean).join(' — ') ||
                                                        '—'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th>Bus route</th>
                                                <td>{routeName || (student.Current_Route_Id ? `#${student.Current_Route_Id}` : '—')}</td>
                                            </tr>
                                            <tr>
                                                <th>Status</th>
                                                <td>{student.Status_Type_Id}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body p-md-4">
                            <h5 className="mb-3">Challan type &amp; options</h5>
                            <div className="row g-2 mb-3">
                                <div className="col-12">
                                    <label className="form-label d-block">Generate</label>
                                    <div className="d-flex flex-wrap gap-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="ss-challan-kind"
                                                id="ss-kind-monthly"
                                                checked={challanKind === 'monthly'}
                                                onChange={() => setChallanKind('monthly')}
                                                disabled={isBusy}
                                            />
                                            <label className="form-check-label" htmlFor="ss-kind-monthly">
                                                <strong>Monthly</strong> challan
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="ss-challan-kind"
                                                id="ss-kind-admission"
                                                checked={challanKind === 'admission'}
                                                onChange={() => setChallanKind('admission')}
                                                disabled={isBusy}
                                            />
                                            <label className="form-check-label" htmlFor="ss-kind-admission">
                                                <strong>Admission</strong> challan (New Student List API)
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="ss-challan-kind"
                                                id="ss-kind-advance"
                                                checked={challanKind === 'advance'}
                                                onChange={() => setChallanKind('advance')}
                                                disabled={isBusy}
                                            />
                                            <label className="form-check-label" htmlFor="ss-kind-advance">
                                                <strong>Advance</strong> challan (fee 020)
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {challanKind === 'admission' && student && !hideAdmissionInfo && (
                                <div
                                    className={`alert alert-dismissible fade show ${String(student.Registration_No || '').trim() ? 'alert-warning' : 'alert-info'} small py-2 mb-3`}
                                    role="alert"
                                >
                                    {String(student.Registration_No || '').trim() ? (
                                        <>
                                            This student has a <strong>registration number</strong>. Admission posting
                                            only applies to <strong>unregistered</strong> students — the server will skip
                                            this student (same rule as{' '}
                                            <Link to="/students/NewStudentList">New Student List</Link>).
                                        </>
                                    ) : (
                                        <>
                                            Unregistered student: eligible if class has <strong>Admission (A)</strong>{' '}
                                            fees or transport applies, and no Admission challan exists yet.
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setHideAdmissionInfo(true)}
                                    />
                                </div>
                            )}

                            {challanKind === 'advance' && student && !hideAdvanceInfo && (
                                <div
                                    className={`alert alert-dismissible fade show ${String(student.Registration_No || '').trim() ? 'alert-info' : 'alert-warning'} small py-2 mb-3`}
                                    role="alert"
                                >
                                    {String(student.Registration_No || '').trim() ? (
                                        <>
                                            Registered student: advance challan will use fee code <strong>020</strong>{' '}
                                            (Advance Fee) on <strong>tblChalanHead</strong> + <strong>tblADLedger</strong>.
                                            Payment against this challan will be handled in a later step.
                                        </>
                                    ) : (
                                        <>
                                            This student has <strong>no registration number</strong>. Advance challan
                                            requires a registered student.
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setHideAdvanceInfo(true)}
                                    />
                                </div>
                            )}

                            {challanKind === 'monthly' ? (
                                <div className="row align-items-center">
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Month</label>
                                            <select
                                                className="form-select"
                                                value={Month}
                                                onChange={(e) => setMonth(e.target.value)}
                                                disabled={isBusy}
                                            >
                                                <option value="">Please Select</option>
                                                {MONTH_OPTIONS.map((m) => (
                                                    <option key={m.value} value={m.value}>
                                                        {m.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Year</label>
                                            <select
                                                className="form-select"
                                                value={fYearsLoaded ? Year : ''}
                                                onChange={(e) => setYear(e.target.value)}
                                                disabled={isBusy || !fYearsLoaded}
                                            >
                                                {!fYearsLoaded ? (
                                                    <option value="">Loading…</option>
                                                ) : (
                                                    <>
                                                        <option value="">Please Select</option>
                                                        {fYearList.map((item, index) => (
                                                            <option key={index} value={String(item.FYear)}>
                                                                {item.FYear}
                                                            </option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group">
                                            <label>Challan due date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={dueDate}
                                                onChange={(e) => setDueDate(e.target.value)}
                                                disabled={isBusy}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-5 text-md-end d-flex align-items-end justify-content-md-end pb-2">
                                        <button
                                            type="button"
                                            className="button button-primary"
                                            onClick={() => {
                                                scrollToTop();
                                                handlePostChallan();
                                            }}
                                            disabled={
                                                isBusy || !student || !fYearsLoaded || !fYearList.length
                                            }
                                            style={{ minWidth: '200px' }}
                                        >
                                            {busyMode === 'post' ? 'Posting…' : 'Generate monthly challan'}
                                        </button>
                                    </div>
                                    <div className="col-12">
                                        <div className="form-check border rounded px-3 py-2 bg-light mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="ss-post-transport"
                                                checked={postTransportFee}
                                                onChange={(e) => setPostTransportFee(e.target.checked)}
                                                disabled={isBusy}
                                            />
                                            <label className="form-check-label" htmlFor="ss-post-transport">
                                                <strong>Post transport fee</strong> (005) when the student has a bus route
                                                — same behaviour as Start New Month (Challan).
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ) : challanKind === 'advance' ? (
                                <div className="row align-items-end">
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Month</label>
                                            <select
                                                className="form-select"
                                                value={Month}
                                                onChange={(e) => setMonth(e.target.value)}
                                                disabled={isBusy}
                                            >
                                                <option value="">Please Select</option>
                                                {MONTH_OPTIONS.map((m) => (
                                                    <option key={m.value} value={m.value}>
                                                        {m.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Year</label>
                                            <select
                                                className="form-select"
                                                value={fYearsLoaded ? Year : ''}
                                                onChange={(e) => setYear(e.target.value)}
                                                disabled={isBusy || !fYearsLoaded}
                                            >
                                                {!fYearsLoaded ? (
                                                    <option value="">Loading…</option>
                                                ) : (
                                                    <>
                                                        <option value="">Please Select</option>
                                                        {fYearList.map((item, index) => (
                                                            <option key={index} value={String(item.FYear)}>
                                                                {item.FYear}
                                                            </option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Due date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={dueDate}
                                                onChange={(e) => setDueDate(e.target.value)}
                                                disabled={isBusy}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group">
                                            <label>Advance amount (Rs.)</label>
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                className="form-control"
                                                value={advanceAmount}
                                                onChange={(e) => setAdvanceAmount(e.target.value)}
                                                disabled={isBusy}
                                                placeholder="e.g. 15000"
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Line description</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={advanceDescription}
                                                onChange={(e) => setAdvanceDescription(e.target.value)}
                                                disabled={isBusy}
                                                maxLength={500}
                                            />
                                            <span className="text-muted small d-block mt-1">
                                                Fee code <strong>020</strong> · type name Advance Fee
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-12 text-md-end pb-1">
                                        <button
                                            type="button"
                                            className="button button-primary"
                                            onClick={() => {
                                                scrollToTop();
                                                handlePostAdvanceChallan();
                                            }}
                                            disabled={
                                                isBusy ||
                                                !student ||
                                                !String(student.Registration_No || '').trim() ||
                                                !fYearsLoaded ||
                                                !fYearList.length
                                            }
                                            style={{ minWidth: '220px' }}
                                        >
                                            {busyMode === 'postAdvance'
                                                ? 'Posting…'
                                                : 'Generate advance challan'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="row align-items-end">
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label>Admission challan due date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={admissionDueDate}
                                                onChange={(e) => setAdmissionDueDate(e.target.value)}
                                                disabled={isBusy}
                                            />
                                            <span className="text-muted small d-block mt-1">
                                                Same field as on New Student List (defaults to end of current month).
                                            </span>
                                        </div>
                                    </div>
                                    <div className="col-md-8 text-md-end pb-1">
                                        <button
                                            type="button"
                                            className="button button-primary"
                                            onClick={() => {
                                                scrollToTop();
                                                handlePostAdmissionChallan();
                                            }}
                                            disabled={isBusy || !student}
                                            style={{ minWidth: '220px' }}
                                        >
                                            {busyMode === 'postAdmission'
                                                ? 'Posting…'
                                                : 'Generate admission challan'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {lastChalanHeadId != null && lastChalanHeadId > 0 && !hideLastPostedInfo && (
                                <div className="alert alert-success alert-dismissible fade show mt-3 mb-0" role="alert">
                                    <div className="fw-semibold">Last posted challan</div>
                                    <div className="small">
                                        Head ID: <code>{lastChalanHeadId}</code>
                                        {lastChallanNo ? (
                                            <>
                                                {' '}
                                                · Challan no.: <code>{lastChallanNo}</code>
                                            </>
                                        ) : null}
                                    </div>
                                    <Link
                                        className="button button-white mt-2 d-inline-flex align-items-center gap-1"
                                        style={{ fontSize: '0.875rem', padding: '6px 14px' }}
                                        to={`/students/PrintChalanHead/?ChalanHeadIds=${lastChalanHeadId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Open print view
                                        <i className="ri-printer-line"></i>
                                    </Link>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setHideLastPostedInfo(true)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SingleStudentMonthlyChallan;
